import React from "react";
import { makeObjectFromArrayMap, Row } from "@hex-insights/core";
import {
	BooleanField,
	DateTimeField,
	FormType,
	SearchDropDownOption,
	TextAreaInput,
	TextField,
	TextFieldInputProps,
} from "@hex-insights/forms";
import { useAttendanceStatusListQuery } from "../../../Utilities";
import { AttendanceStatusBadge } from "../../Components/AttendanceStatus";
import { SchoolAttendanceRecordFormValues } from "../../FormUtilities";
import { AttendanceStatusSelectField } from "../AttendanceStatus";
import { BaseFieldProps } from "../shared";
import { StudentSelectField } from "../Student";
import { UserSelectField } from "../User";

/**
 * Generic props for fields of the SchoolAttendanceRecord model.
 */
type FieldProps<K extends keyof SchoolAttendanceRecordFormValues.Base = keyof SchoolAttendanceRecordFormValues.Base> =
	BaseFieldProps<Pick<SchoolAttendanceRecordFormValues.Base, K>>;

/**
 * Generic props for fields of the SchoolAttendanceRecord model that only appear in the detail form.
 */
type DetailFieldProps<
	K extends keyof SchoolAttendanceRecordFormValues.Detail = keyof SchoolAttendanceRecordFormValues.Detail,
> = BaseFieldProps<Pick<SchoolAttendanceRecordFormValues.Detail, K>>;

/**
 * Field component for the `createdAt` field of the SchoolAttendanceRecord model.
 */
export function CreatedAt({ formState, ...props }: DetailFieldProps<"createdAt">) {
	return (
		<DateTimeField
			formState={formState}
			name="createdAt"
			optional={FormType.isCreate(formState.formType)}
			precision="minute"
			{...props}
		/>
	);
}

/**
 * Field component for the `attendanceDate` field of the SchoolAttendanceRecord model.
 */
export function AttendanceDate({ formState, ...props }: FieldProps<"attendanceDate">) {
	return <DateTimeField formState={formState} name="attendanceDate" precision="day" {...props} />;
}

/**
 * Field component for the `arrivalTime` field of the SchoolAttendanceRecord model.
 */
export function ArrivalTime({ formState, ...props }: FieldProps<"arrivalTime">) {
	return <DateTimeField formState={formState} name="arrivalTime" optional precision="minute" {...props} />;
}

/**
 * Field component for the `departureTime` field of the SchoolAttendanceRecord model.
 */
export function DepartureTime({ formState, ...props }: FieldProps<"departureTime">) {
	return <DateTimeField formState={formState} name="departureTime" optional precision="minute" {...props} />;
}

/**
 * Field component for the `notes` field of the SchoolAttendanceRecord model.
 */
export function Notes({ formState, ...props }: FieldProps<"notes">) {
	return <TextField formState={formState} name="notes" multiLine optional Input={NotesInput} {...props} />;
}

function NotesInput(props: TextFieldInputProps) {
	return <TextAreaInput minRows={2} {...props} />;
}

/**
 * Field component for the `wasSetByCourseAttendance` field of the SchoolAttendanceRecord model.
 */
export function WasSetByCourseAttendance({ formState, ...props }: DetailFieldProps<"wasSetByCourseAttendance">) {
	return <BooleanField formState={formState} name="wasSetByCourseAttendance" {...props} />;
}

export namespace AttendanceStatus {
	export type SelectProps = FieldProps<"attendanceStatusID"> &
		Omit<
			AttendanceStatusSelectField.SelectProps<SchoolAttendanceRecordFormValues.Base>,
			"formState" | "name" | "optional"
		>;

	/**
	 * Field component for the `attendanceStatus` edge of the CourseAttendanceRecord model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		const { data } = useAttendanceStatusListQuery();
		const attendanceStatusMap = React.useMemo(
			() => makeObjectFromArrayMap(data?.attendanceStatusConnection.edges ?? [], (e) => [e.node.id, e.node]),
			[data],
		);

		const displayOption = React.useCallback(
			({ value, label }: SearchDropDownOption<string | null>) => {
				if (value === null) {
					return "";
				}

				const instance = attendanceStatusMap[value];
				return (
					<Row justify="spaced-start" align="center" horizontalSpacing="0.5rem">
						<AttendanceStatusBadge attendanceStatus={instance} style={{ fontSize: "0.875rem" }} />
						{label}
					</Row>
				);
			},
			[attendanceStatusMap],
		);

		return (
			<AttendanceStatusSelectField.Select
				formState={formState}
				name="attendanceStatusID"
				displayOption={displayOption}
				{...props}
			/>
		);
	}
}

export namespace Author {
	export type SelectProps = DetailFieldProps<"authorID"> &
		Omit<UserSelectField.SelectProps<SchoolAttendanceRecordFormValues.Detail>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `author` edge of the SchoolAttendanceRecord model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <UserSelectField.Select formState={formState} name="authorID" optional {...props} />;
	}
}

export namespace Student {
	export type SelectProps = FieldProps<"studentID"> &
		Omit<StudentSelectField.SelectProps<SchoolAttendanceRecordFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `student` edge of the SchoolAttendanceRecord model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <StudentSelectField.Select formState={formState} name="studentID" {...props} />;
	}
}
